import {
  Button,
  Divider,
  makeStyles,
  mergeClasses,
  Text,
  tokens,
  Tooltip,
} from "@fluentui/react-components";
import { Copy32Filled } from "@fluentui/react-icons";
import React, { useState } from "react";
import { useNotification } from "../../../../contextApis/NotificationContext";
import { DrayageQuote, QuoteTollsEntry } from "../../../../types/drayage";
import { LoadingTypesEnum } from "../../../../types/loading";
import { copyToClipboardAddin } from "../../../../utils/copy";
import { formatToCurrency } from "../../../../utils/currency";
import { formatMiles } from "../../../../utils/miles";
import OfferPrice from "../OfferPrice/OfferPrice";
import RouteConnector from "../RouteCard/RouteConnector/RouteConnector";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
    height: "100%",
  },
  closeButton: {
    lineHeight: tokens.lineHeightBase100,
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightBold,
  },
  contentWrapper: {
    width: "100%",
    height: "100%",
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: "7px",
  },
  content: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
    borderRadius: "7px",
  },
  routeInfo: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    paddingBottom: "8px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  detailedCostInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
    paddingBottom: "8px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  costInfoGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    width: "100%",
  },
  detailedRow: {
    display: "flex",
    flexDirection: "row",
    gap: "8px",
    width: "100%",
  },
  textWidth: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    flex: "0 0 92px",
    maxWidth: "92px",
  },
  alignEnd: {
    width: "92px",
    textAlign: "end",
  },
  doubleItem: {
    display: "flex",
    flexDirection: "row",
    gap: "8px",
    width: "100%",
  },
  grayedOut: {
    color: tokens.colorNeutralForeground3,
  },
  costInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
    cursor: "pointer",
    paddingBottom: "8px",
    marginBottom: "8px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  costInfoRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: "8px",
    width: "100%",
  },
  priceRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "8px",
    width: "100%",
  },
  itemBlue: {
    color: tokens.colorPaletteBlueForeground2,
  },
});

interface RouteDetailsCardProps {
  quote: DrayageQuote;
  tollsData?: QuoteTollsEntry;
  tollsLoading: LoadingTypesEnum;
  onHandleOfferPrice: (
    quoteResultId: number,
    offeredPrice: number,
    isAccepted: boolean,
  ) => void;
  onClose?: () => void;
}

const RouteDetailsCard: React.FC<RouteDetailsCardProps> = ({
  quote,
  tollsData,
  tollsLoading,
  onHandleOfferPrice,
  onClose,
}) => {
  const { addNotification } = useNotification();

  const styles = useStyles();
  const [offeredPrice, setOfferedPrice] = useState<number | "">("");

  const companyDriverNetProfit =
    offeredPrice !== "" ? Number(offeredPrice) - quote.totalExpense : 0;
  const companyDriverNetProfitMargin =
    companyDriverNetProfit !== 0
      ? (companyDriverNetProfit / +offeredPrice) * 100
      : 0;

  const offeredPriceOwnerOperatorNetProfit =
    offeredPrice !== "" ? Number(offeredPrice) - quote.ownerOperatorPay : 0;
  const offeredPriceOwnerOperatorNetProfitMargin =
    offeredPriceOwnerOperatorNetProfit !== 0
      ? (offeredPriceOwnerOperatorNetProfit / +offeredPrice) * 100
      : 0;
  const tollsCount =
    (tollsData?.goingRouteTolls.length || 0) +
    (tollsData?.returnRouteTolls.length || 0);

  const onCopy = async () => {
    try {
      const template = `Pickup Location: ${quote.pickupLocation}\nDelivery Location: ${quote.deliveryLocation}\nRound Trip: ${formatMiles(quote.roundtripMiles).formattedValue} miles\nLoad Price: ${formatToCurrency(quote.quotePrice)}`;
      await copyToClipboardAddin(template);

      addNotification({
        type: "success",
        message: "Route copied to clipboard",
      });
    } catch (error) {
      addNotification({
        type: "error",
        message: "Failed to copy route to clipboard",
      });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.contentWrapper}>
        <div className={styles.content}>
          <div className={styles.routeInfo}>
            <RouteConnector
              left={quote.pickupLocation}
              right={quote.deliveryLocation}
            />

            <Tooltip content={"Close"} relationship="label">
              <Button
                className={styles.closeButton}
                onClick={onClose}
                appearance="transparent"
                size="small"
              >
                X
              </Button>
            </Tooltip>
          </div>

          <div className={styles.detailedCostInfo}>
            <div className={styles.costInfoGroup}>
              <Text weight="semibold">Company Driver</Text>

              <div
                className={mergeClasses(styles.detailedRow, styles.grayedOut)}
              >
                <Text className={styles.textWidth}>Driver Pay</Text>
                <Text className={styles.alignEnd}>
                  {formatToCurrency(quote.companyDriverPay)}
                </Text>
              </div>

              <div
                className={mergeClasses(styles.detailedRow, styles.grayedOut)}
              >
                <Text className={styles.textWidth}>Truck</Text>
                <Text className={styles.alignEnd}>
                  {formatToCurrency(quote.truck)}
                </Text>
              </div>

              <div
                className={mergeClasses(styles.detailedRow, styles.grayedOut)}
              >
                <Text className={styles.textWidth}>Fuel</Text>
                <Text className={styles.alignEnd}>
                  {formatToCurrency(quote.fuel)}
                </Text>
              </div>

              <div
                className={mergeClasses(styles.detailedRow, styles.grayedOut)}
              >
                <Text className={styles.textWidth}>Insurance</Text>
                <Text className={styles.alignEnd}>
                  {formatToCurrency(quote.insurance)}
                </Text>
              </div>

              <div
                className={mergeClasses(styles.detailedRow, styles.grayedOut)}
              >
                <Text className={styles.textWidth}>Tolls</Text>

                <div className={styles.doubleItem}>
                  <Text className={styles.alignEnd}>
                    {tollsLoading === LoadingTypesEnum.pending && (
                      <span>Loading...</span>
                    )}
                    {tollsLoading === LoadingTypesEnum.rejected && (
                      <>rejected</>
                    )}
                    {tollsLoading === LoadingTypesEnum.fulfilled &&
                      `${formatToCurrency(tollsData?.totalTollsCost ?? 0)}`}
                  </Text>
                  <Text>{tollsCount ? `(${tollsCount})` : ""}</Text>
                </div>
              </div>

              <div
                className={mergeClasses(styles.detailedRow, styles.grayedOut)}
              >
                <Text className={styles.textWidth}>Total expense</Text>
                <Text className={styles.alignEnd}>
                  {formatToCurrency(quote.totalExpense)}
                </Text>
              </div>

              <div
                className={mergeClasses(styles.detailedRow, styles.grayedOut)}
              >
                <Text className={styles.textWidth}>Net</Text>

                <div className={styles.doubleItem}>
                  <Text className={styles.alignEnd}>
                    {formatToCurrency(quote.netProfit)}
                  </Text>
                  <Text>({quote.netProfitMargin}%)</Text>
                </div>
              </div>
            </div>

            <Divider />

            <div className={styles.costInfoGroup}>
              <Text weight="semibold">Owner Operator</Text>

              <div
                className={mergeClasses(styles.detailedRow, styles.grayedOut)}
              >
                <Text className={styles.textWidth}>O/O Pay</Text>
                <Text className={styles.alignEnd}>
                  {formatToCurrency(quote.ownerOperatorPay)}
                </Text>
              </div>

              <div
                className={mergeClasses(styles.detailedRow, styles.grayedOut)}
              >
                <Text className={styles.textWidth}>O/O Net</Text>

                <div className={styles.doubleItem}>
                  <Text className={styles.alignEnd}>
                    {formatToCurrency(quote.ownerOperatorNetProfit)}
                  </Text>
                  <Text>({quote.ownerOperatorNetProfitMargin}%)</Text>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.costInfo} onClick={onCopy}>
            <div className={styles.costInfoRow}>
              <Text className={styles.textWidth}>Linehaul</Text>
              <Text className={styles.alignEnd}>
                {formatToCurrency(quote.linehaulPrice)}
              </Text>
              <Text>
                {formatMiles(quote.roundtripMiles).formattedValue} miles
              </Text>
            </div>
            <div className={`${styles.costInfoRow} ${styles.grayedOut}`}>
              <Text className={styles.textWidth}>FSC</Text>
              <div className={styles.doubleItem}>
                <Text className={styles.alignEnd}>
                  {formatToCurrency(quote.fuelSurchargePrice)}
                </Text>
                <Text>({quote.fuelSurchargePercentage}%)</Text>
              </div>
            </div>
            <div className={styles.costInfoRow}>
              <Text className={styles.textWidth}>Total</Text>
              <Text className={styles.alignEnd}>
                {formatToCurrency(quote.quotePrice)}
              </Text>
            </div>
          </div>

          <Tooltip content={"Copy Route"} relationship="label">
            <Button
              style={{
                position: "absolute",
                bottom: "10px",
                right: "0px",
              }}
              onClick={onCopy}
              appearance="transparent"
              size="small"
            >
              <Copy32Filled />
            </Button>
          </Tooltip>
        </div>
      </div>

      <OfferPrice
        onAccepted={(offer) =>
          onHandleOfferPrice(quote.quoteResultId, offer, true)
        }
        onDeclined={(offer) =>
          onHandleOfferPrice(quote.quoteResultId, offer, false)
        }
        onOfferPriceChange={(offer) => setOfferedPrice(offer)}
      />

      <div className={styles.priceRow}>
        <Text className={mergeClasses(styles.itemBlue, styles.textWidth)}>
          Offer C/D Net
        </Text>
        <Text className={styles.itemBlue}>
          {formatToCurrency(companyDriverNetProfit)}
        </Text>
        <Text className={styles.itemBlue}>
          (
          {companyDriverNetProfitMargin > 0
            ? companyDriverNetProfitMargin.toFixed(0)
            : 0}
          %)
        </Text>
      </div>

      <div className={styles.priceRow}>
        <Text className={mergeClasses(styles.itemBlue, styles.textWidth)}>
          Offer O/O Pay
        </Text>
        <Text className={styles.itemBlue}>
          {formatToCurrency(offeredPriceOwnerOperatorNetProfit)}
        </Text>
        <Text className={styles.itemBlue}>
          (
          {offeredPriceOwnerOperatorNetProfitMargin > 0
            ? offeredPriceOwnerOperatorNetProfitMargin.toFixed(0)
            : 0}
          %)
        </Text>
      </div>
    </div>
  );
};

export default RouteDetailsCard;
